import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Papa from 'papaparse';
import { Container, Row, Col, Form, Button, ProgressBar, Table, Alert } from 'react-bootstrap';

function ImportExportEmails() {
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState([]);
  const [groups, setGroups] = useState([]); // List of groups from DB
  const [selectedGroup, setSelectedGroup] = useState(''); // Selected group
  const [passFilter, setPassFilter] = useState(1); // Default value is 1
  const [optoutFilter, setOptoutFilter] = useState(0); // Default value is 0
  const [deletedFilter, setDeletedFilter] = useState(0); // Default value is 0
  const [sendsFilter, setSendsFilter] = useState(null); // Default value is 0
  const [validatedFilter, setValidatedFilter] = useState(0); // Default value is 0
  const [sendsOptions, setSendsOptions] = useState([]); // Array for `sends` options


  // Fetch available groups from the backend
  useEffect(() => {
    axios
      .get('https://api.eyorsolutions.com/api/groups')
      .then((response) => setGroups(response.data))
      .catch((error) => console.error('Error fetching groups:', error));
  }, []);

  useEffect(() => {
    axios
      .get('https://api.eyorsolutions.com/api/sends')
      .then((response) => setSendsOptions(response.data))
      .catch((error) => console.error('Error fetching sends options:', error));
  }, []);  

  // Handle CSV Export
  const handleExportToCSV = () => {
    const params = new URLSearchParams({
      group: selectedGroup || '',
      pass: passFilter,
      optout: optoutFilter,
      deleted: deletedFilter,
      sends: sendsFilter,
      validated: validatedFilter,
    }).toString();

    axios
      .get(`https://api.eyorsolutions.com/api/export-csv?${params}`, { responseType: 'blob' })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Filtered_Group_${selectedGroup || 'All'}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch((error) => console.error('Error exporting CSV:', error));
  };
  

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const parseName = (fullname) => {
    let firstName = '';
    let lastName = '';

    fullname = fullname.trim();
    if (fullname.includes(',')) {
      const [last, first] = fullname.split(',');
      firstName = first ? first.trim() : '';
      lastName = last ? last.trim() : '';
    } else {
      const nameParts = fullname.split(' ').filter(Boolean);

      if (nameParts.length === 1) {
        firstName = nameParts[0];
      } else {
        firstName = nameParts.slice(0, -1).join(' ');
        lastName = nameParts[nameParts.length - 1];
      }
    }

    return {
      firstName: firstName ? capitalize(firstName) : '',
      lastName: lastName ? capitalize(lastName) : '',
    };
  };

  const capitalize = (str) =>
    str
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());

  const handleFileUpload = () => {
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const rows = results.data;
        let processedCount = 0;
        let totalRows = rows.length;

        for (const row of rows) {
          const { email, fullname, group } = row;
          let firstName = '';
          let lastName = '';

          if (fullname) {
            const parsedName = parseName(fullname);
            firstName = parsedName.firstName;
            lastName = parsedName.lastName;
          }

          try {
            const payload = {
              email: email.toLowerCase(),
              group,
              ...(fullname && { first_name: firstName, last_name: lastName }),
            };

            const response = await axios.post('https://api.eyorsolutions.com/api/validate', payload);

            setResults((prev) => [
              ...prev,
              { email, result: response.data },
            ]);

            processedCount++;
            setProgress((processedCount / totalRows) * 100);
          } catch (error) {
            console.error(`Error validating email ${email}:`, error);
          }
        }
      },
    });
  };

  return (
    <Container className="py-4">
      <h1 className="mb-4">Import & Export Emails</h1>

      <Row className="mb-4">
        <Col>
          <Form.Group controlId="groupSelect">
            <Form.Label>Select Group</Form.Label>
            <Form.Select
              value={selectedGroup}
              onChange={(e) => setSelectedGroup(e.target.value)}
            >
              <option value="">All Groups</option>
              {groups.map((group, index) => (
                <option key={index} value={group}>
                  {group}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col>
          <Form.Group controlId="passFilter">
            <Form.Label>Pass</Form.Label>
            <Form.Check
              type="checkbox"
              label="Only Passed"
              checked={passFilter === 1}
              onChange={(e) => setPassFilter(e.target.checked ? 1 : 0)}
            />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group controlId="optoutFilter">
            <Form.Label>Optout</Form.Label>
            <Form.Check
              type="checkbox"
              label="Exclude Opted Out"
              checked={optoutFilter === 0}
              onChange={(e) => setOptoutFilter(e.target.checked ? 0 : 1)}
            />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group controlId="deletedFilter">
            <Form.Label>Deleted</Form.Label>
            <Form.Check
              type="checkbox"
              label="Exclude Deleted"
              checked={deletedFilter === 0}
              onChange={(e) => setDeletedFilter(e.target.checked ? 0 : 1)}
            />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group controlId="sendsFilter">
            <Form.Label>Sends</Form.Label>
            <Form.Select
              value={sendsFilter}
              onChange={(e) => setSendsFilter(e.target.value)}
            >
              <option value="null">null</option>
              {sendsOptions.map((value, index) => (
                <option key={index} value={value}>
                  {value}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col>
          <Form.Group controlId="validatedFilter">
            <Form.Label>Validated</Form.Label>
            <Form.Check
              type="checkbox"
              label="Only Validated"
              checked={validatedFilter === 1}
              onChange={(e) => setValidatedFilter(e.target.checked ? 1 : 0)}
            />
          </Form.Group>
        </Col>
      </Row>
      <Button onClick={handleExportToCSV}>Export to CSV</Button>

      <Row className="mb-4">
        <Col>
          <Form.Group controlId="csvUpload">
            <Form.Label>Upload CSV File</Form.Label>
            <Form.Control type="file" accept=".csv" onChange={handleFileChange} />
          </Form.Group>
        </Col>
        <Col className="d-flex align-items-end">
          <Button
            onClick={handleFileUpload}
            disabled={!file}
            variant="success"
          >
            Upload & Validate
          </Button>
        </Col>
      </Row>

      {progress > 0 && (
        <Row className="mb-4">
          <Col>
            <h4>Progress</h4>
            <ProgressBar now={progress} label={`${progress.toFixed(2)}%`} />
          </Col>
        </Row>
      )}

      {results.length > 0 && (
        <Row>
          <Col>
            <h4>Validation Results</h4>
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {results
                  .filter((result) =>
                    ['Invalid', 'Duplicate'].includes(result.result.status)
                  )
                  .map((result, index) => (
                    <tr key={index}>
                      <td>{result.email}</td>
                      <td>
                        <span className="text-danger">{result.result.status}</span>
                      </td>
                      <td>{result.result.reason}</td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </Col>
        </Row>
      )}

      {results.length === 0 && progress === 100 && (
        <Alert variant="success" className="mt-4">
          All emails are valid!
        </Alert>
      )}
    </Container>
  );
}

export default ImportExportEmails;
